import {
  Plane,
  Hotel,
  CarFront,
  Package,
  Clock3,
  MapPin,
  BadgeCheck,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { dateInTimeZone, formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { TravelItinerary, TravelSegment, TravelSegmentType } from '@/lib/types';

const TYPE_META: Record<
  TravelSegmentType,
  { label: string; icon: React.ComponentType<{ className?: string }>; accent: string; badge: string }
> = {
  flight: {
    label: 'Flight',
    icon: Plane,
    accent: 'border-sky-500 bg-sky-50/80',
    badge: 'bg-sky-100 text-sky-800',
  },
  lodging: {
    label: 'Lodging',
    icon: Hotel,
    accent: 'border-violet-500 bg-violet-50/80',
    badge: 'bg-violet-100 text-violet-800',
  },
  ground: {
    label: 'Ground',
    icon: CarFront,
    accent: 'border-emerald-500 bg-emerald-50/80',
    badge: 'bg-emerald-100 text-emerald-800',
  },
  other: {
    label: 'Other',
    icon: Package,
    accent: 'border-slate-500 bg-slate-50/80',
    badge: 'bg-slate-100 text-slate-800',
  },
};

function formatDateTime(value: string | null, timeZone: string) {
  if (!value) return '—';
  return formatDateTimeInTimeZone(value, timeZone, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDay(value: string) {
  return formatDateOnly(value, '—', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function groupSegments(segments: TravelSegment[], timeZone: string) {
  return [...segments]
    .sort((a, b) => {
      const timeDelta = a.starts_at.localeCompare(b.starts_at);
      if (timeDelta !== 0) return timeDelta;
      return a.order - b.order;
    })
    .reduce<Array<{ day: string; segments: TravelSegment[] }>>((groups, segment) => {
      const day = dateInTimeZone(segment.starts_at, timeZone);
      const last = groups[groups.length - 1];
      if (!last || last.day !== day) {
        groups.push({ day, segments: [segment] });
      } else {
        last.segments.push(segment);
      }
      return groups;
    }, []);
}

export function TravelItineraryCards({ itinerary }: { itinerary: TravelItinerary }) {
  const timeZone = useOrgTimeZone();
  const groups = groupSegments(itinerary.segments, timeZone);
  const confirmed = itinerary.is_confirmed;

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-gray-200 shadow-sm">
        <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle className="text-xl text-gray-900">{itinerary.project_name}</CardTitle>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span className="inline-flex items-center gap-1.5">
                  <BadgeCheck className="h-4 w-4 text-emerald-600" />
                  {itinerary.tech_name}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  {itinerary.assignment_summary.location || itinerary.assignment_summary.location_fk_name || '—'}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock3 className="h-4 w-4 text-slate-400" />
                  {formatDateOnly(itinerary.assignment_summary.start_date)}
                  {itinerary.assignment_summary.end_date &&
                    ` → ${formatDateOnly(itinerary.assignment_summary.end_date)}`}
                </span>
              </div>
            </div>
            <Badge
              className={confirmed
                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                : 'bg-amber-100 text-amber-800 border-amber-200'}
            >
              {confirmed ? 'Confirmed' : 'Needs confirmation'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            <InfoStat label="Project number" value={itinerary.assignment_summary.project_number || '—'} />
            <InfoStat label="Assignment location" value={itinerary.assignment_summary.location || '—'} />
            <InfoStat
              label="Confirmation"
              value={confirmed ? 'Confirmed' : 'Pending'}
            />
          </div>
          {itinerary.notes && (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
              <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <FileText className="h-3.5 w-3.5" />
                Notes
              </p>
              <p className="whitespace-pre-wrap text-sm text-slate-700">{itinerary.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {groups.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-slate-500">
            No travel segments have been added yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {groups.map(({ day, segments }) => (
            <div key={day} className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  {formatDay(day)}
                </h3>
              </div>
              <div className="space-y-3">
                {segments.map((segment) => (
                  <SegmentCard key={segment.id} segment={segment} timeZone={timeZone} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InfoStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-white p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function SegmentCard({ segment, timeZone }: { segment: TravelSegment; timeZone: string }) {
  const meta = TYPE_META[segment.segment_type];
  const Icon = meta.icon;

  return (
    <Card className={`overflow-hidden border-l-4 ${meta.accent}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${meta.badge}`}>
              <Icon className="h-4.5 w-4.5" />
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-base font-semibold text-slate-900">{segment.title}</h4>
                <Badge className={meta.badge}>{meta.label}</Badge>
              </div>
              <p className="text-sm text-slate-600">
                {formatDateTime(segment.starts_at, timeZone)}
                {segment.ends_at && ` — ${formatDateTime(segment.ends_at, timeZone)}`}
              </p>
            </div>
          </div>
          {segment.confirmation_code && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Confirmation code
              </p>
              <p className="font-mono text-sm font-semibold text-slate-900">
                {segment.confirmation_code}
              </p>
            </div>
          )}
        </div>

        {(segment.details || segment.notes) && (
          <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
            {segment.details && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Details
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{segment.details}</p>
              </div>
            )}
            {segment.notes && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Notes
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{segment.notes}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
